import type {
  AgeAssessmentOutcome,
  PoaIdFailureReason,
  PoaIdType,
  PoaOrderFlowRecord,
  RsaRefusalReason,
  RsaRefusalRecord,
} from './poa.types'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { STORAGE_KEYS } from '@/shared/lib/storage'

interface PoaFlowStore {
  records: Record<string, PoaOrderFlowRecord>
  setRsaOutcome: (orderId: string, outcome: 'passed' | 'refused') => void
  setRsaRefusalDraft: (
    orderId: string,
    draft: Partial<{ reason: RsaRefusalReason | null, otherReason: string }>,
  ) => void
  recordRsaRefusal: (record: RsaRefusalRecord) => void
  setAgeOutcome: (orderId: string, outcome: AgeAssessmentOutcome) => void
  setIdType: (orderId: string, idType: PoaIdType) => void
  /** Pass `undefined` to take the answer back — the reasons toggle, they do not latch. */
  setIdFailureReason: (orderId: string, reason?: PoaIdFailureReason) => void
  clearOrder: (orderId: string) => void
  clearAll: () => void
}

function emptyRecord(orderId: string): PoaOrderFlowRecord {
  return { orderId, updatedAt: Date.now() }
}

/**
 * Local, order-keyed POA working state. It deliberately persists across reloads so
 * an interrupted handover can resume — the team member does not lose answers to a
 * dropped connection or a backgrounded app in a car park.
 *
 * It is working state only, never a record of truth. Once the backend has accepted
 * the submission the entry is removed (`clearOrder`), so completed compliance data
 * does not linger in `localStorage`: the order document is the system of record
 * from that point, and the local copy would only be a stale duplicate taking up
 * space. Cleanup is deliberately keyed to a *successful* submission — if the call
 * fails the entry survives so the handover can be retried without re-answering.
 */
export const usePoaFlowStore = create<PoaFlowStore>()(
  persist(
    set => ({
      records: {},

      setRsaOutcome: (orderId, outcome) => set((state) => {
        const current = state.records[orderId] ?? emptyRecord(orderId)
        return {
          records: {
            ...state.records,
            [orderId]: {
              ...current,
              rsaOutcome: outcome,
              rsaRecordedAt: new Date().toISOString(),
              // A pass supersedes any previously confirmed refusal/removal.
              rsaRefusal: outcome === 'passed' ? undefined : current.rsaRefusal,
              updatedAt: Date.now(),
            },
          },
        }
      }),

      setRsaRefusalDraft: (orderId, draft) => set((state) => {
        const current = state.records[orderId] ?? emptyRecord(orderId)
        const currentDraft = current.rsaRefusalDraft ?? { reason: null, otherReason: '' }
        return {
          records: {
            ...state.records,
            [orderId]: {
              ...current,
              rsaRefusalDraft: { ...currentDraft, ...draft },
              updatedAt: Date.now(),
            },
          },
        }
      }),

      recordRsaRefusal: record => set((state) => {
        const current = state.records[record.orderId] ?? emptyRecord(record.orderId)
        return {
          records: {
            ...state.records,
            [record.orderId]: {
              ...current,
              rsaOutcome: 'refused',
              rsaRecordedAt: current.rsaRecordedAt ?? new Date().toISOString(),
              rsaRefusalDraft: {
                reason: record.reason,
                otherReason: record.otherReason ?? '',
              },
              rsaRefusal: record,
              updatedAt: Date.now(),
            },
          },
        }
      }),

      setAgeOutcome: (orderId, outcome) => set((state) => {
        const current = state.records[orderId] ?? emptyRecord(orderId)
        return {
          records: {
            ...state.records,
            [orderId]: {
              ...current,
              ageOutcome: outcome,
              ageRecordedAt: new Date().toISOString(),
              // Looking 25+ supersedes any previous ID result.
              idType: outcome === 'looks-25-or-older' ? undefined : current.idType,
              idFailureReason: outcome === 'looks-25-or-older' ? undefined : current.idFailureReason,
              idRecordedAt: outcome === 'looks-25-or-older' ? undefined : current.idRecordedAt,
              updatedAt: Date.now(),
            },
          },
        }
      }),

      setIdType: (orderId, idType) => set((state) => {
        const current = state.records[orderId] ?? emptyRecord(orderId)
        return {
          records: {
            ...state.records,
            [orderId]: {
              ...current,
              ageOutcome: 'id-required',
              idType,
              // The ID sighted and the reason it failed are recorded independently:
              // a team member can check a learner's permit and still refuse supply
              // because it shows the customer is under 18.
              idRecordedAt: new Date().toISOString(),
              updatedAt: Date.now(),
            },
          },
        }
      }),

      setIdFailureReason: (orderId, idFailureReason) => set((state) => {
        const current = state.records[orderId] ?? emptyRecord(orderId)
        // Taking the answer back leaves no ID answer at all unless a type was also
        // sighted, so the timestamp goes with it rather than claiming a recording
        // that no longer exists.
        const hasIdAnswer = Boolean(idFailureReason) || Boolean(current.idType)
        return {
          records: {
            ...state.records,
            [orderId]: {
              ...current,
              ageOutcome: 'id-required',
              idFailureReason,
              idRecordedAt: hasIdAnswer ? new Date().toISOString() : undefined,
              updatedAt: Date.now(),
            },
          },
        }
      }),

      clearOrder: orderId => set((state) => {
        const records = { ...state.records }
        delete records[orderId]
        return { records }
      }),

      clearAll: () => set({ records: {} }),
    }),
    {
      name: STORAGE_KEYS.POA_FLOW,
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({ records: state.records }),
    },
  ),
)
